import React, { useEffect, useState } from "react";
import { db, storage } from "./firebase";
import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  doc,
  deleteDoc,
  updateDoc,
} from "firebase/firestore";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

import Switcher from "./switcher.js";
import SignOutButton from "./SignOutButton.js";

const Dashboard = () => {
  const [items, setItems] = useState([]);
  const [file, setFile] = useState(null);
  const [selectedTag, setSelectedTag] = useState("");
  const [selectedType, setSelectedType] = useState("video");
  const [title, setTitle] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [newTag, setNewTag] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");

  const fetchData = async () => {
    let data = [];
    for (const type of ["videos", "images"]) {
      const q = query(collection(db, type), orderBy("timestamp", "desc"));
      const snapshot = await getDocs(q);
      snapshot.forEach((docSnap) => {
        const d = docSnap.data();
        data.push({
          id: docSnap.id,
          title: d.title,
          tags: d.tags,
          url: d.videoURL || d.imageURL,
          path: d.videoPath || d.imagePath,
          timestamp: d.timestamp?.toDate?.() || new Date(),
          type,
        });
      });
    }
    // Sort all items by timestamp desc (across both collections)
    data.sort((a, b) => b.timestamp - a.timestamp);
    setItems(data);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedTag || !file || !title.trim()) {
      alert("All fields are required.");
      return;
    }

    setIsUploading(true);
    const fileName = `${Date.now()}-${file.name}`;
    const folder = selectedType === "image" ? "images" : "videos";
    const storageRef = ref(storage, `${folder}/${fileName}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(progress);
      },
      (error) => {
        console.error("Upload error", error);
        alert("Error during upload");
        setIsUploading(false);
      },
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        const data = {
          title,
          tags: [selectedTag],
          timestamp: new Date(),
        };

        if (selectedType === "image") {
          data.imageURL = url;
          data.imagePath = `${folder}/${fileName}`;
        } else {
          data.videoURL = url;
          data.videoPath = `${folder}/${fileName}`;
        }

        const docRef = await addDoc(collection(db, folder), data);

        setItems((prev) => [
          {
            id: docRef.id,
            ...data,
            url,
            path: data.imagePath || data.videoPath,
            type: folder,
          },
          ...prev,
        ]);

        // Reset form
        setUploadProgress(0);
        setIsUploading(false);
        setTitle("");
        setFile(null);
        setSelectedTag("");
        setSelectedType("video");

        alert("✅ Upload completed!");
      }
    );
  };

  const handleDelete = async (id, path, type) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    await deleteDoc(doc(db, type, id));
    await deleteObject(ref(storage, path));
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleEdit = (id, title, tags) => {
    setEditingId(id);
    setNewTitle(title);
    setNewTag(tags[0]);
  };

  const handleUpdate = async (id, type) => {
    if (!newTitle || !newTag) {
      alert("All fields required");
      return;
    }

    await updateDoc(doc(db, type, id), {
      title: newTitle,
      tags: [newTag],
    });

    setItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, title: newTitle, tags: [newTag] } : item
      )
    );

    setEditingId(null);
    setNewTitle("");
    setNewTag("");
  };

  return (
    <div className="section_admin">
      <Switcher />
      <div className="section_title_new padd-15" style={{ textAlign: "center", marginBottom: "10px" }}>
        <h2>Admin Dashboard</h2>
        <SignOutButton />
      </div>

      <form className="upload-form" onSubmit={handleUpload}>
        <input type="file" onChange={(e) => setFile(e.target.files[0])} />
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title"
          className="input-field"
        />
        <select
          value={selectedTag}
          onChange={(e) => setSelectedTag(e.target.value)}
          className="dropdown"
        >
          <option value="">Choose a tag</option>
          <option value="3D">3D</option>
          <option value="vfx">VFX</option>
          <option value="animation">Animation</option>
          <option value="motion">Motion Graphics</option>
          <option value="graphics">Graphics Design</option>
        </select>
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="dropdown"
        >
          <option value="video">Video</option>
          <option value="image">Image</option>
        </select>
        <button type="submit" className="upload-button">Upload</button>
      </form>

      {isUploading && (
        <div className="progress-container">
          <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
        </div>
      )}

      <div className="container">
        <div className="filters scroll-y filter-container">
          {["all", "3D", "vfx", "animation", "motion", "graphics"].map((tag) => (
            <span
              key={tag}
              className={`filter__item__button ${activeFilter === tag ? "active-work" : ""}`}
              onClick={() => setActiveFilter(tag)}
            >
              {tag === "all" ? "All" : tag.charAt(0).toUpperCase() + tag.slice(1)}
            </span>
          ))}
        </div>

        <div className="row_new">
          {items
            .filter((item) => activeFilter === "all" || item.tags.includes(activeFilter))
            .map((item) => (
              <div key={item.id} className="portfolio-item padd-15 project-card" style={{ height: "250px" }}>
                <div className="portfolio-item-inner" style={{ height: "250px" }}>
                  {item.type === "videos" ? (
                    <div className="video-wrapper">
                      <video width="100%" height="auto" controls muted>
                        <source src={item.url} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  ) : (
                    <div className="image-wrapper">
                      <img
                        src={item.url}
                        alt={item.title}
                        style={{
                          width: "100%",
                          height: "150px",
                          objectFit: "cover",
                          display: "block",
                          borderRadius: "10px",
                        }}
                      />
                    </div>
                  )}

                  <div className="buttons-overlay">
                    <button onClick={() => handleEdit(item.id, item.title, item.tags)} className="dashboard_edit_del_btn">Edit</button>
                    <button onClick={() => handleDelete(item.id, item.path, item.type)} className="dashboard_edit_del_btn">Delete</button>
                  </div>

                  <h3 className="video_title_tag">{item.title}</h3>
                  <p className="section_title_new video_title_tag" style={{ marginBottom: "10px" }}>
                    Tags: {item.tags.join(", ")}
                  </p>

                  {editingId === item.id && (
                    <div className="edit-form">
                      <input
                        type="text"
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        placeholder="Enter new title"
                        required
                      />
                      <select
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        required
                      >
                        <option value="3D">3D</option>
                        <option value="vfx">VFX</option>
                        <option value="animation">Animation</option>
                        <option value="motion">Motion</option>
                        <option value="graphics">Graphics</option>
                      </select>
                      <button onClick={() => handleUpdate(item.id, item.type)}>Submit</button>
                      <button onClick={() => setEditingId(null)}>Cancel</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
